// Eskom PARCC — QA-notes thread on a working paper (Lindiwe feedback Jun 23 2026, 4a).
//
// Managers/admins leave QA notes on a reviewer's working paper; the reviewer is
// alerted (notification + email) and the whole thread is visible WITHIN the working
// paper itself. The thread is read-only for reviewers; the add box + resolve toggle
// render only for QA-capable roles (managers + admins, gated by `canQa`). Writes go
// through admin-api (server-enforced + alerting), wired by the parent.
import { useState } from 'react';
import { Button, PersonChip } from '../../components/ui';
import type { NameDirectory } from '../../lib/people';
import type { QaNoteRead } from '../../lib/db';

export function QaNotesPanel({
  notes,
  canQa,
  directory,
  busy,
  onAdd,
  onResolve,
}: {
  notes: readonly QaNoteRead[];
  /** May the current user add / resolve QA notes (manager or admin)? */
  canQa: boolean;
  directory: NameDirectory;
  busy: boolean;
  onAdd: (note: string) => void;
  onResolve: (id: string, resolved: boolean) => void;
}): React.JSX.Element | null {
  const [draft, setDraft] = useState('');

  // Reviewers with no notes and no add affordance see nothing (keeps the row tidy).
  if (notes.length === 0 && !canQa) return null;

  function submit(): void {
    const text = draft.trim();
    if (!text) return;
    onAdd(text);
    setDraft('');
  }

  return (
    <div className="wp-qa" style={{ marginTop: 12, borderTop: '1px solid var(--line)', paddingTop: 12 }}>
      <div className="small" style={{ fontWeight: 700, marginBottom: 6 }}>
        QA notes{notes.length ? ` (${notes.length})` : ''}
      </div>

      {notes.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
          {notes.map((n) => (
            <li key={n.id} className={`wp-qa-note${n.resolved ? ' resolved' : ''}`}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {n.addedByEmail ? (
                  <PersonChip email={n.addedByEmail} directory={directory} />
                ) : (
                  <span className="small">QA</span>
                )}
                <span
                  className="badge"
                  style={{ color: n.resolved ? 'var(--ok)' : 'var(--warn)', borderColor: 'currentColor' }}
                >
                  {n.resolved ? 'Resolved' : 'Open'}
                </span>
                <span className="small" style={{ color: 'var(--faint)' }}>
                  {new Date(n.createdAt).toLocaleString('en-ZA')}
                </span>
              </div>
              <div style={{ marginTop: 4, whiteSpace: 'pre-wrap' }}>{n.note}</div>
              {canQa && (
                <div style={{ marginTop: 4 }}>
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => onResolve(n.id, !n.resolved)}>
                    {n.resolved ? 'Re-open' : 'Mark resolved'}
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      {canQa && (
        <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <textarea
            value={draft}
            disabled={busy}
            placeholder="Leave a QA note for the reviewer to action…"
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
          />
          <div>
            <Button size="sm" disabled={busy || !draft.trim()} onClick={submit}>
              {busy ? 'Adding…' : 'Add QA note'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
